/**
 * CI/CD Reports Component
 * Displays threat modeling and compliance scan reports from CI/CD pipelines
 */

import React, { useState, useEffect } from 'react';
import {
  GitBranch,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Eye,
  Filter,
  RefreshCw,
  FileText,
  Shield,
  Target,
  ChevronDown,
  ChevronRight,
  X,
  Terminal,
  Calendar,
  Search
} from 'lucide-react';

const CICDReports = ({ api }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    jobType: '',
    search: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    running: 0
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      // Only show jobs that originated from API (CI/CD pipelines)
      const response = await api.listJobs({
        status: filters.status || null,
        source: 'api',
        limit: 100
      });
      setJobs(response.jobs || []);

      // Calculate stats
      const jobList = response.jobs || [];
      setStats({
        total: jobList.length,
        completed: jobList.filter(j => j.status === 'completed').length,
        failed: jobList.filter(j => j.status === 'failed').length,
        running: jobList.filter(j => j.status === 'running' || j.status === 'pending').length
      });
    } catch (err) {
      setError('Failed to load reports: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadJobDetail = async (jobId) => {
    try {
      setLoadingDetail(true);
      const detail = await api.getJobStatus(jobId);
      setSelectedJob(detail);
    } catch (err) {
      setError('Failed to load job details: ' + err.message);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDownload = async (jobId, fileType) => {
    try {
      const data = await api.downloadFile(jobId, fileType);

      let blob;
      let filename;

      if (fileType === 'json') {
        blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        filename = `report_${jobId}.json`;
      } else if (fileType === 'markdown') {
        const reportContent = typeof data === 'object' ? data.report : data;
        blob = new Blob([reportContent], { type: 'text/markdown' });
        filename = `report_${jobId}.md`;
      } else {
        blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        filename = `${fileType}_${jobId}.json`;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + err.message);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={18} className="status-icon completed" />;
      case 'failed':
        return <XCircle size={18} className="status-icon failed" />;
      case 'running':
        return <RefreshCw size={18} className="status-icon running" />;
      case 'pending':
        return <Clock size={18} className="status-icon pending" />;
      default:
        return <Clock size={18} className="status-icon" />;
    }
  };

  const getJobTypeIcon = (jobType) => {
    switch (jobType) {
      case 'threat_modeling':
      case 'client1_threat_modeling':
        return <Target size={18} />;
      case 'compliance_scanning':
        return <Shield size={18} />;
      case 'github_mcp_scan':
        return <GitBranch size={18} />;
      default:
        return <FileText size={18} />;
    }
  };

  const getJobTypeName = (jobType) => {
    switch (jobType) {
      case 'threat_modeling':
      case 'client1_threat_modeling':
        return 'Threat Model';
      case 'compliance_scanning':
        return 'Compliance Scan';
      case 'github_mcp_scan':
        return 'GitHub MCP Scan';
      default:
        return jobType;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const filteredJobs = jobs.filter(job => {
    if (filters.status && job.status !== filters.status) return false;
    if (filters.jobType && job.job_type !== filters.jobType) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        job.job_id?.toLowerCase().includes(search) ||
        job.system_type?.toLowerCase().includes(search) ||
        job.github_repo?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const renderDetailModal = () => {
    if (!selectedJob) return null;

    return (
      <div className="modal-overlay" onClick={() => setSelectedJob(null)}>
        <div className="cicd-detail-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">
              {getJobTypeIcon(selectedJob.job_type)}
              <div>
                <h2>{getJobTypeName(selectedJob.job_type)} Report</h2>
                <span className="job-id">ID: {selectedJob.job_id}</span>
              </div>
            </div>
            <button className="modal-close" onClick={() => setSelectedJob(null)}>
              <X size={24} />
            </button>
          </div>

          <div className="modal-content">
            {/* Status Banner */}
            <div className={`status-banner status-${selectedJob.status}`}>
              {getStatusIcon(selectedJob.status)}
              <span>Status: {selectedJob.status.toUpperCase()}</span>
              {selectedJob.progress && <span className="progress-text">{selectedJob.progress}</span>}
            </div>

            {/* Job Info */}
            <div className="job-info-grid">
              <div className="info-item">
                <label>Created</label>
                <span>{formatDate(selectedJob.created_at)}</span>
              </div>
              <div className="info-item">
                <label>Completed</label>
                <span>{formatDate(selectedJob.completed_at)}</span>
              </div>
              <div className="info-item">
                <label>System Type</label>
                <span>{selectedJob.system_type || 'N/A'}</span>
              </div>
              {selectedJob.github_repo && (
                <div className="info-item">
                  <label>Repository</label>
                  <span>{selectedJob.github_repo}</span>
                </div>
              )}
            </div>

            {/* Results Summary */}
            {selectedJob.status === 'completed' && selectedJob.results && (
              <div className="results-section">
                <h3>Results Summary</h3>

                <div className="results-stats">
                  {selectedJob.results.summary && (
                    <>
                      <div className="result-stat">
                        <AlertTriangle size={24} color="#e74c3c" />
                        <div>
                          <span className="stat-number">
                            {selectedJob.results.summary.total_threats || selectedJob.results.threat_model?.length || 0}
                          </span>
                          <span className="stat-label">Threats Found</span>
                        </div>
                      </div>
                      <div className="result-stat">
                        <Shield size={24} color="#16a085" />
                        <div>
                          <span className="stat-number">
                            {selectedJob.results.summary.frameworks_applied?.length || 0}
                          </span>
                          <span className="stat-label">Frameworks</span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Compliance specific */}
                  {selectedJob.job_type === 'compliance_scanning' && (
                    <>
                      <div className="result-stat">
                        <Target size={24} color="#3b82f6" />
                        <div>
                          <span className="stat-number">{selectedJob.overall_score?.toFixed(1) || 'N/A'}</span>
                          <span className="stat-label">Score</span>
                        </div>
                      </div>
                      <div className="result-stat">
                        <CheckCircle size={24} color="#10b981" />
                        <div>
                          <span className="stat-number">
                            {selectedJob.compliant_controls || 0}/{selectedJob.total_controls || 0}
                          </span>
                          <span className="stat-label">Compliant</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Golden Controls Section */}
                {selectedJob.results.golden_controls_mapping && (
                  <div className="golden-controls-section">
                    <h4>
                      <Shield size={20} />
                      Golden Controls Mapping
                    </h4>

                    {/* Coverage Stats */}
                    <div className="golden-controls-stats">
                      <div className="gc-stat">
                        <span className="gc-stat-number">{selectedJob.results.golden_controls_mapping.mapped_controls || 0}</span>
                        <span className="gc-stat-label">Controls Mapped</span>
                      </div>
                      <div className="gc-stat">
                        <span className="gc-stat-number">{Object.keys(selectedJob.results.golden_controls_mapping.coverage_by_category || {}).length}</span>
                        <span className="gc-stat-label">Categories</span>
                      </div>
                      <div className="gc-stat">
                        <span className="gc-stat-number">{selectedJob.results.golden_controls_mapping.total_threats || 0}</span>
                        <span className="gc-stat-label">Threats Addressed</span>
                      </div>
                    </div>

                    {/* Coverage by Category */}
                    {selectedJob.results.golden_controls_mapping.coverage_by_category && (
                      <div className="gc-coverage">
                        <h5>Coverage by Category</h5>
                        <div className="gc-categories">
                          {Object.entries(selectedJob.results.golden_controls_mapping.coverage_by_category).map(([category, count]) => (
                            <div key={category} className="gc-category-item">
                              <span className="gc-category-name">{category}</span>
                              <span className="gc-category-count">{count} threats</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Control Details */}
                    {selectedJob.results.golden_controls_mapping.threat_control_mapping?.length > 0 && (
                      <div className="gc-mappings">
                        <h5>Control Mappings ({selectedJob.results.golden_controls_mapping.threat_control_mapping.length} total)</h5>
                        <div className="gc-mappings-list">
                          {selectedJob.results.golden_controls_mapping.threat_control_mapping.map((mapping, idx) => (
                            <div key={idx} className="gc-mapping-item">
                              <div className="gc-threat-ref">
                                <span className="gc-threat-id">Threat #{mapping.threat_id}</span>
                                <p className="gc-threat-scenario">{mapping.threat_scenario?.substring(0, 100)}...</p>
                              </div>
                              <div className="gc-controls">
                                {mapping.mapped_controls?.map((control, cIdx) => (
                                  <div key={cIdx} className="gc-control-badge">
                                    <span className={`gc-risk-indicator ${control.risk_level?.toLowerCase()}`}>
                                      {control.risk_level === 'Critical' ? '🔴' : '🟡'}
                                    </span>
                                    <span className="gc-control-id">{control.control_id}</span>
                                    <span className="gc-control-title">{control.title}</span>
                                    {control.automation_possible && <span className="gc-auto-badge">🤖</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Full Threat List */}
                {selectedJob.results.threat_model?.length > 0 && (
                  <div className="threat-list-full">
                    <h4>All Identified Threats ({selectedJob.results.threat_model.length})</h4>
                    <div className="threats-container">
                      {selectedJob.results.threat_model.map((threat, idx) => (
                        <div key={idx} className="threat-card">
                          <div className="threat-card-header">
                            <span className="threat-id">Threat #{threat.id || idx + 1}</span>
                            {threat.cvss_score && (
                              <span className={`cvss-badge severity-${threat.cvss_score >= 7 ? 'high' : threat.cvss_score >= 4 ? 'medium' : 'low'}`}>
                                CVSS {threat.cvss_score}
                              </span>
                            )}
                          </div>
                          <p className="threat-scenario">{threat.threat_scenario || threat.description}</p>

                          <div className="threat-frameworks">
                            {threat.stride_categories?.length > 0 && (
                              <div className="framework-tags">
                                <span className="framework-label">STRIDE:</span>
                                {threat.stride_categories.map((cat, i) => (
                                  <span key={i} className="framework-tag stride">{cat}</span>
                                ))}
                              </div>
                            )}
                            {threat.owasp_categories?.length > 0 && (
                              <div className="framework-tags">
                                <span className="framework-label">OWASP:</span>
                                {threat.owasp_categories.map((cat, i) => (
                                  <span key={i} className="framework-tag owasp">{cat}</span>
                                ))}
                              </div>
                            )}
                            {threat.mitre_tactics?.length > 0 && (
                              <div className="framework-tags">
                                <span className="framework-label">MITRE:</span>
                                {threat.mitre_tactics.map((tactic, i) => (
                                  <span key={i} className="framework-tag mitre">{tactic}</span>
                                ))}
                              </div>
                            )}
                            {threat.maestro_layers?.length > 0 && (
                              <div className="framework-tags">
                                <span className="framework-label">MAESTRO:</span>
                                {threat.maestro_layers.map((layer, i) => (
                                  <span key={i} className="framework-tag maestro">{layer}</span>
                                ))}
                              </div>
                            )}
                          </div>

                          {threat.mitigation && (
                            <div className="threat-mitigation">
                              <strong>Mitigation:</strong>
                              <p>{threat.mitigation}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* No Results Warning */}
            {selectedJob.status === 'completed' && !selectedJob.results && (
              <div className="warning-section">
                <AlertTriangle size={24} color="#f59e0b" />
                <div>
                  <h4>Results Not Available</h4>
                  <p>The job completed but the results were not saved due to a temporary issue. Please re-run the analysis from your CI/CD pipeline.</p>
                </div>
              </div>
            )}

            {/* Error Display */}
            {selectedJob.status === 'failed' && (
              <div className="error-section">
                <AlertTriangle size={24} />
                <div>
                  <h4>Error Details</h4>
                  <p>{selectedJob.error || 'Unknown error occurred'}</p>
                </div>
              </div>
            )}

            {/* Download Section */}
            {selectedJob.status === 'completed' && (
              <div className="download-section">
                <h3>Download Reports</h3>
                <div className="download-buttons">
                  <button onClick={() => handleDownload(selectedJob.job_id, 'json')}>
                    <Download size={16} />
                    JSON Report
                  </button>
                  <button onClick={() => handleDownload(selectedJob.job_id, 'markdown')}>
                    <Download size={16} />
                    Markdown Report
                  </button>
                  {selectedJob.results?.export_files?.sarif && (
                    <button onClick={() => handleDownload(selectedJob.job_id, 'sarif')}>
                      <Download size={16} />
                      SARIF
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="cicd-reports loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading CI/CD reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cicd-reports">
      {/* Header */}
      <div className="cicd-header">
        <div className="cicd-header-left">
          <Terminal size={32} color="#667eea" />
          <div>
            <h1>CI/CD Scan Reports</h1>
            <p>View threat modeling and compliance reports from your pipelines</p>
          </div>
        </div>
        <button className="refresh-btn" onClick={loadJobs} disabled={loading}>
          <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="cicd-stats">
        <div className="stat-card">
          <div className="stat-icon total">
            <FileText size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total Reports</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon completed">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-number">{stats.completed}</span>
            <span className="stat-label">Completed</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon failed">
            <XCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-number">{stats.failed}</span>
            <span className="stat-label">Failed</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon running">
            <RefreshCw size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-number">{stats.running}</span>
            <span className="stat-label">In Progress</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="cicd-filters">
        <div className="filter-group">
          <Filter size={18} />
          <select
            value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="running">Running</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div className="filter-group">
          <Target size={18} />
          <select
            value={filters.jobType}
            onChange={e => setFilters(f => ({ ...f, jobType: e.target.value }))}
          >
            <option value="">All Types</option>
            <option value="threat_modeling">Threat Modeling</option>
            <option value="compliance_scanning">Compliance Scan</option>
          </select>
        </div>
        <div className="filter-group search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by ID, system type, or repo..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <AlertTriangle size={18} />
          {error}
        </div>
      )}

      {/* Reports List */}
      <div className="reports-list">
        {filteredJobs.length === 0 ? (
          <div className="empty-state">
            <Terminal size={48} />
            <h3>No Reports Found</h3>
            <p>
              {jobs.length === 0
                ? "Run a scan from your CI/CD pipeline to see reports here."
                : "No reports match your current filters."}
            </p>
            <div className="pipeline-hint">
              <h4>Quick Start</h4>
              <p>Add this to your GitHub Actions workflow:</p>
              <code>
                curl -X POST https://agenticthreat.ai/api/cicd/analyze \<br/>
                &nbsp;&nbsp;-H "X-API-Token: $API_TOKEN" \<br/>
                &nbsp;&nbsp;-H "Content-Type: application/json" \<br/>
                &nbsp;&nbsp;-d '&#123;"system_description": "...",<br/>
                &nbsp;&nbsp;&nbsp;&nbsp;"repository_url": "owner/repo",<br/>
                &nbsp;&nbsp;&nbsp;&nbsp;"branch": "main"&#125;'
              </code>
            </div>
          </div>
        ) : (
          <table className="reports-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Type</th>
                <th>System</th>
                <th>Created</th>
                <th>Threats</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map(job => (
                <tr key={job.job_id} className={`status-row-${job.status}`}>
                  <td>
                    <div className="status-cell">
                      {getStatusIcon(job.status)}
                      <span>{job.status}</span>
                    </div>
                  </td>
                  <td>
                    <div className="type-cell">
                      {getJobTypeIcon(job.job_type)}
                      <span>{getJobTypeName(job.job_type)}</span>
                    </div>
                  </td>
                  <td>
                    <span className="system-type">{job.system_type || 'general'}</span>
                    {job.github_repo && (
                      <span className="repo-name">{job.github_repo.split('/').pop()}</span>
                    )}
                  </td>
                  <td>
                    <div className="date-cell">
                      <Calendar size={14} />
                      <span>{formatDate(job.created_at)}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`threat-count ${job.threat_count > 0 ? 'has-threats' : ''}`}>
                      {job.threat_count || 0}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        onClick={() => loadJobDetail(job.job_id)}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      {job.status === 'completed' && (
                        <button
                          className="action-btn download"
                          onClick={() => handleDownload(job.job_id, 'json')}
                          title="Download JSON"
                        >
                          <Download size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {renderDetailModal()}

      {/* Loading Overlay */}
      {loadingDetail && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading report details...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CICDReports;
